import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash, FaPlus, FaTags } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import { LoadingState } from '../../components/Loading';
import Modal from '../../components/Modal';

const emptyCategory = {
  name: '',
  slug: '',
  description: '',
  isActive: true,
  order: 0,
};

const AdminCategories = () => {
  const toast = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyCategory);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { loadCategories(); }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/categories');
      setCategories(res.data);
    } catch (e) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing('new');
    setFormData(emptyCategory);
  };

  const openEdit = (cat) => {
    setEditing(cat._id);
    setFormData({
      name: cat.name,
      slug: cat.slug,
      description: cat.description || '',
      isActive: cat.isActive,
      order: cat.order || 0,
    });
  };

  const closeModal = () => setEditing(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });

    if (name === 'name' && editing === 'new') {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post('/api/categories', formData);
        toast.success('Category created');
      } else {
        await axios.put(`/api/categories/${editing}`, formData);
        toast.success('Category updated');
      }
      closeModal();
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`/api/categories/${deleteTarget._id}`);
      toast.success('Category deleted');
      setDeleteTarget(null);
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="admin-header">
        <div>
          <h1>Categories</h1>
          <p className="admin-header-subtitle">{categories.length} categories</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>
          <FaPlus /> Add category
        </button>
      </div>

      {loading ? (
        <LoadingState />
      ) : categories.length === 0 ? (
        <div className="card empty-state">
          <FaTags className="empty-state-icon" />
          <h3>No categories yet</h3>
          <p>Create your first category to organize products</p>
          <button className="btn btn-primary" onClick={openNew}>
            <FaPlus /> Add category
          </button>
        </div>
      ) : (
        <div className="admin-table">
          <div className="admin-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Description</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat._id}>
                    <td><strong>{cat.name}</strong></td>
                    <td style={{ color: 'var(--gray-500)', fontSize: 13, fontFamily: 'monospace' }}>{cat.slug}</td>
                    <td style={{ color: 'var(--gray-600)', fontSize: 13, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {cat.description || '—'}
                    </td>
                    <td>{cat.order || 0}</td>
                    <td>
                      <span className={`badge ${cat.isActive ? 'success' : 'neutral'}`}>
                        {cat.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon btn-primary" onClick={() => openEdit(cat)} title="Edit">
                          <FaEdit />
                        </button>
                        <button className="btn-icon btn-danger" onClick={() => setDeleteTarget(cat)} title="Delete">
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!editing}
        onClose={closeModal}
        title={editing === 'new' ? 'New category' : 'Edit category'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label>Name *</label>
            <input type="text" name="name" required value={formData.name} onChange={handleChange} autoFocus />
          </div>
          <div className="form-group">
            <label>Slug *</label>
            <input type="text" name="slug" required value={formData.slug} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows="3" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Display order</label>
              <input type="number" name="order" value={formData.order} onChange={handleChange} />
              <small>Lower numbers appear first</small>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 28 }}>
              <input
                type="checkbox" name="isActive" id="isActiveCat"
                checked={formData.isActive} onChange={handleChange}
                style={{ width: 18, height: 18, accentColor: 'var(--primary-600)' }}
              />
              <label htmlFor="isActiveCat" style={{ marginBottom: 0, cursor: 'pointer' }}>Active</label>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete category?"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              Delete
            </button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          Products in this category will need to be reassigned.
        </p>
      </Modal>
    </div>
  );
};

export default AdminCategories;
